
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import AdminProductsList from "@/components/admin/AdminProductsList";
import AdminProductForm from "@/components/admin/AdminProductForm";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { EditingProduct, SizeGuide } from "@/types/product";

const AdminProducts = () => {
  const [isCreating, setIsCreating] = useState(false);
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  console.log("AdminProducts: Rendering with editingProductId:", editingProductId);
  
  const { data: editingProduct, isLoading: isLoadingProduct } = useQuery({
    queryKey: ['admin-product', editingProductId],
    queryFn: async () => {
      if (!editingProductId) return null;
      console.log("AdminProducts: Fetching product for editing:", editingProductId);

      const { data, error } = await supabase
        .from('products')
        .select(`
          id,
          name,
          description,
          price,
          published,
          has_colors,
          has_sizes,
          has_size_guide,
          size_guide,
          product_categories (
            categories (
              name
            )
          ),
          product_variants (
            size,
            color_name,
            color_hex,
            position
          ),
          product_images (
            url,
            main
          )
        `)
        .eq('id', editingProductId)
        .maybeSingle();

      if (error) {
        console.error("AdminProducts: Error fetching product:", error);
        toast({
          title: "Erreur",
          description: "Impossible de charger le produit",
          variant: "destructive",
        });
        throw error;
      }

      if (!data) {
        throw new Error("Produit non trouvé");
      }

      console.log("AdminProducts: Product data fetched:", data);

      // Trier les variants par position
      const sortedVariants = [...data.product_variants].sort((a, b) => 
        (a.position || 0) - (b.position || 0)
      );

      // Extraire les couleurs uniquement si has_colors est true
      const uniqueColors = data.has_colors ? Array.from(
        new Set(
          sortedVariants
            .filter((v: any) => v.color_name && v.color_hex)
            .map((variant: any) => JSON.stringify({
              name: variant.color_name,
              hex: variant.color_hex
            }))
        )
      ).map(str => JSON.parse(str)) : [];

      // Extraire les tailles uniquement si has_sizes est true
      const selectedSizes = data.has_sizes ? Array.from(
        new Set(sortedVariants.map((variant: any) => variant.size))
      ).filter(Boolean) as string[] : [];

      const mappedProduct = {
        id: data.id,
        name: data.name,
        description: data.description,
        price: data.price,
        categories: data.product_categories.map((pc: any) => pc.categories.name),
        published: data.published,
        size_guide: data.size_guide as SizeGuide | null,
        variants: sortedVariants.map((variant: any) => ({
          size: variant.size,
          colorName: variant.color_name,
          colorHex: variant.color_hex,
          position: variant.position
        })),
        colors: uniqueColors,
        selectedSizes: selectedSizes,
        images: data.product_images.map((image: any) => image.url),
        has_colors: data.has_colors,
        has_sizes: data.has_sizes,
        has_size_guide: data.has_size_guide
      } as EditingProduct;

      console.log("AdminProducts: Mapped product data:", mappedProduct);
      return mappedProduct;
    },
    enabled: !!editingProductId
  });

  const handleSuccess = async () => {
    console.log("AdminProducts: Handling success - Starting cache invalidation");
    
    try {
      // Invalider les queries de manière ordonnée
      await queryClient.invalidateQueries({ queryKey: ['admin-products'] });
      await queryClient.invalidateQueries({ queryKey: ['admin-product', editingProductId] });
      await queryClient.invalidateQueries({ queryKey: ['products'] });

      // Réinitialiser l'état
      setIsCreating(false);
      setEditingProductId(null);

      // Notification
      toast({
        title: editingProduct ? "Produit modifié" : "Produit créé",
        description: `Le produit a été ${editingProduct ? "modifié" : "créé"} avec succès`,
      });

      console.log("AdminProducts: Cache invalidation completed successfully");
    } catch (error) {
      console.error("AdminProducts: Error during cache invalidation:", error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la mise à jour des données",
        variant: "destructive",
      });
    }
  };

  const shouldShowForm = isCreating || (editingProductId && !isLoadingProduct && editingProduct);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold">Gestion des produits</h1>
        {!shouldShowForm && (
          <Button 
            onClick={() => setIsCreating(true)}
            className="flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Nouveau produit
          </Button>
        )}
      </div>

      {shouldShowForm ? (
        <AdminProductForm
          productToEdit={editingProduct}
          onSuccess={handleSuccess}
          onCancel={() => {
            console.log("AdminProducts: Cancelling edit/create");
            setIsCreating(false);
            setEditingProductId(null);
            queryClient.removeQueries({ queryKey: ['admin-product', editingProductId] });
          }}
        />
      ) : (
        <AdminProductsList 
          onEdit={(productId) => {
            console.log("AdminProducts: Edit requested for product:", productId);
            setEditingProductId(productId);
          }}
        />
      )}
    </div>
  );
};

export default AdminProducts;
